import React, { useState } from 'react';
import Layout from '../components/layout/Layout';
import { FiSettings, FiShield, FiSliders, FiMoon, FiSun, FiUser, FiBarChart2 } from '../utils/icons';
import { useLanguage } from '../utils/languageContext';
import { useTheme } from '../utils/ThemeContext';

const Settings = () => {
  const { language, setLanguage, translations } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const isDarkMode = theme === 'dark';
  
  // Language options
  const languages = [
    { value: 'en', label: translations.english, flag: '🇺🇸' },
    { value: 'zh', label: translations.chinese, flag: '🇨🇳' },
    { value: 'fr', label: translations.french, flag: '🇫🇷' },
    { value: 'ja', label: translations.japanese, flag: '🇯🇵' },
  ];
  
  const handleLanguageChange = (value: string) => {
    setLanguage(value as 'en' | 'zh' | 'fr' | 'ja');
  };
  
  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-text mb-2">{translations.settings}</h1>
          <p className="text-text/70">
            Adjust your application preferences.
          </p>
        </div>
        
        <div className="space-y-6">
          {/* Language Settings */}
          <div className="bg-foreground rounded-xl p-6 shadow-sm border border-border">
            <div className="flex items-center mb-6">
              <div className="p-2 rounded-lg bg-primary/10 text-primary mr-4">
                <FiBarChart2 size={24} />
              </div>
              <h2 className="text-xl font-semibold">{translations.language}</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {languages.map((lang) => (
                <button
                  key={lang.value}
                  onClick={() => handleLanguageChange(lang.value)}
                  className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                    language === lang.value 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border hover:bg-card-hover'
                  }`}
                >
                  <div className="flex items-center">
                    <span className="text-xl mr-3">{lang.flag}</span>
                    <span className="font-medium">{lang.label}</span>
                  </div>
                  
                  {language === lang.value && (
                    <div className="p-1 rounded-full bg-primary text-white">
                      <FiUser size={16} />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
          
          {/* Appearance Settings */}
          <div className="bg-foreground rounded-xl p-6 shadow-sm border border-border">
            <div className="flex items-center mb-6">
              <div className="p-2 rounded-lg bg-primary/10 text-primary mr-4">
                <FiSliders size={24} />
              </div>
              <h2 className="text-xl font-semibold">Appearance</h2>
            </div>
            
            <div>
              <button 
                onClick={toggleTheme}
                className="flex items-center justify-between w-full p-4 rounded-lg border border-border hover:bg-card-hover transition-colors"
              >
                <div className="flex items-center">
                  <span className="p-2 rounded-full bg-card mr-3">
                    {isDarkMode ? <FiSun size={20} /> : <FiMoon size={20} />}
                  </span>
                  <span className="font-medium">{translations.darkMode}</span>
                </div>
                
                <div className={`w-12 h-6 rounded-full p-1 transition-colors ${isDarkMode ? 'bg-primary' : 'bg-gray-600'}`}>
                  <div className={`h-4 w-4 rounded-full bg-white transform transition-transform ${isDarkMode ? 'translate-x-6' : 'translate-x-0'}`} />
                </div>
              </button>
            </div>
          </div>
          
          {/* Security Settings */}
          <div className="bg-foreground rounded-xl p-6 shadow-sm border border-border">
            <div className="flex items-center mb-6">
              <div className="p-2 rounded-lg bg-primary/10 text-primary mr-4">
                <FiShield size={24} />
              </div>
              <h2 className="text-xl font-semibold">Security</h2>
            </div>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center p-4 rounded-lg border border-border">
                <div>
                  <h3 className="font-medium">Two-Factor Authentication</h3>
                  <p className="text-sm text-text/70">Add an extra layer of security to your account</p>
                </div>
                <div className="w-12 h-6 rounded-full p-1 bg-gray-600">
                  <div className="h-4 w-4 rounded-full bg-white" />
                </div>
              </div>
              
              <div className="flex justify-between items-center p-4 rounded-lg border border-border">
                <div>
                  <h3 className="font-medium">Session Timeout</h3>
                  <p className="text-sm text-text/70">Automatically log out after inactivity</p>
                </div>
                <select className="p-2 rounded-lg bg-card border border-border">
                  <option>30 minutes</option>
                  <option>1 hour</option>
                  <option>4 hours</option>
                  <option>1 day</option>
                </select>
              </div>
              
              <div className="flex justify-between items-center p-4 rounded-lg border border-border">
                <div>
                  <h3 className="font-medium">Login Notifications</h3>
                  <p className="text-sm text-text/70">Get notified of new logins to your account</p>
                </div>
                <div className="w-12 h-6 rounded-full p-1 bg-primary">
                  <div className="h-4 w-4 rounded-full bg-white transform translate-x-6" />
                </div>
              </div>
            </div>
          </div>
          
          {/* Save Button */}
          <div className="flex justify-end">
            <button className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-opacity-90 transition-colors">
              {translations.save}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Settings; 